import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { Pressable, Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import EmojiAddIcon from '@atlaskit/icon/core/migration/emoji-add';
import { fg } from '@atlaskit/platform-feature-flags';

/**
 * Test id for the tooltip
 */
export var RENDER_TOOLTIP_TRIGGER_TESTID = 'render-tooltip-trigger';
export var RENDER_TRIGGER_BUTTON_TESTID = 'render-trigger-button';
var i18n = defineMessages({
  addReaction: {
    id: 'reaction-picker-trigger.add.reaction.message',
    description: 'Message displayed when there are no page reactions existing on the page.',
    defaultMessage: 'Add a reaction'
  }
});
var triggerStyles = xcss({
  minWidth: '32px',
  height: '24px',
  padding: 'space.0',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderRadius: 'border.radius.circle',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  lineHeight: '16px'
});
var subtleTriggerStyles = xcss({
  minWidth: '24px',
  border: 'none'
});
var expandedTriggerStyles = xcss({
  minWidth: '110px'
});
var triggerStylesRefresh = xcss({
  borderRadius: 'border.radius'
});
var transparentEnabledTriggerStyles = xcss({
  borderColor: 'color.border',
  backgroundColor: 'color.background.neutral.subtle',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.neutral.subtle.pressed'
  }
});
var opaqueEnabledTriggerStyles = xcss({
  borderColor: 'color.border',
  backgroundColor: 'elevation.surface',
  ':hover': {
    backgroundColor: 'elevation.surface.hovered'
  },
  ':active': {
    backgroundColor: 'elevation.surface.pressed'
  }
});
var disabledTriggerStyles = xcss({
  borderColor: 'color.border.disabled',
  backgroundColor: 'color.background.disabled'
});
var miniModeStyles = xcss({
  minWidth: '24px',
  padding: 'space.050',
  border: 'none',
  borderRadius: 'border.radius'
});
var addReactionMessageStyles = xcss({
  fontSize: '12px',
  color: 'color.text.subtle',
  marginLeft: 'space.050'
});

/**
 * Render an emoji button to open the reactions select picker
 */
export var Trigger = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var onClick = props.onClick,
    miniMode = props.miniMode,
    tooltipContent = props.tooltipContent,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$ariaAttributes = props.ariaAttributes,
    ariaAttributes = _props$ariaAttributes === void 0 ? {} : _props$ariaAttributes,
    _props$showOpaqueBack = props.showOpaqueBackground,
    showOpaqueBackground = _props$showOpaqueBack === void 0 ? false : _props$showOpaqueBack,
    _props$showAddReactio = props.showAddReactionText,
    showAddReactionText = _props$showAddReactio === void 0 ? false : _props$showAddReactio,
    _props$subtleReaction = props.subtleReactionsSummaryAndPicker,
    subtleReactionsSummaryAndPicker = _props$subtleReaction === void 0 ? false : _props$subtleReaction;
  var handleMouseDown = function handleMouseDown(e, analyticsEvent) {
    if (onClick && !disabled) {
      onClick(e, analyticsEvent);
    }
  };
  return jsx(Tooltip, {
    testId: RENDER_TOOLTIP_TRIGGER_TESTID,
    content: tooltipContent
  }, jsx(Pressable, _extends({
    testId: RENDER_TRIGGER_BUTTON_TESTID,
    xcss: [triggerStyles, subtleReactionsSummaryAndPicker && subtleTriggerStyles, showAddReactionText && expandedTriggerStyles, disabled ? disabledTriggerStyles : showOpaqueBackground ? opaqueEnabledTriggerStyles : transparentEnabledTriggerStyles, miniMode && miniModeStyles, fg('platform-component-visual-refresh') && triggerStylesRefresh],
    onClick: handleMouseDown,
    isDisabled: disabled,
    ref: ref
  }, ariaAttributes), jsx(EmojiAddIcon, {
    color: disabled ? "var(--ds-icon-disabled, #091E424F)" : "var(--ds-icon, #44546F)",
    LEGACY_size: "small",
    label: "Add reaction"
  }), showAddReactionText && jsx(Box, {
    xcss: addReactionMessageStyles
  }, formatMessage(i18n.addReaction))));
});